import React, { Component } from 'react';
import './DrugConcentrationPopup.css';
import Popup from '../../components/popup/Popup';
import FormTemplate from '../../components/templates/formTemplates/FormTemplate';
import Card from '../../components/card/Card';
import Label from '../../components/label/Label';
import Input from '../../inputs/input/Input';
import Button from '../../components/button/Button';
import UniversalDropDown from '../../components/universalDropDown/UniversalDropDown';
import FontIcon from '../../components/fontIcon/FontIcon';
import mainAPI from '../../api/MainAPI';
import toasts from '../../utils/toasts';
import PrimaryButton from "../../components/primaryButton/PrimaryButton";
import SecondaryButton from "../../components/secondaryButton/SecondaryButton";

const numeratorUnitOptions = {
	mcg: 'mcg',
	mg: 'mg',
	g: 'g'
};

const denominatorUnitOptions = {
	ml: 'ml',
	l: 'l'
};

export default class DrugConcentrationPopup extends FormTemplate {
	constructor(props) {
		super(props);
		this.myRefs = {};
		this.state = {
			concentrations: {},
			existingConcentrations: {}
		};
	}

	componentDidMount = async () => {
		await this._loadExistingConcentrations();
	};

	_loadExistingConcentrations = async () => {
		try {
			const [defaultConcentrations, userGroupConcentrations] = await Promise.all([
				mainAPI.drug_concentration.defaults(),
				mainAPI.drug_concentration.forUserGroup()
			]);

			const concentrationMap = {};
			
			if (defaultConcentrations && Array.isArray(defaultConcentrations)) {
				defaultConcentrations.forEach((conc) => {
					if (conc.drug_type_id) {
						concentrationMap[conc.drug_type_id] = {
							numerator_value: conc.numerator_value,
							numerator_unit: conc.numerator_unit,
							denominator_value: conc.denominator_value,
							denominator_unit: conc.denominator_unit
						};
					}
				});
			}

			if (userGroupConcentrations && Array.isArray(userGroupConcentrations)) {
				userGroupConcentrations.forEach((conc) => {
					if (conc.drug_type_id) {
						concentrationMap[conc.drug_type_id] = {
							numerator_value: conc.numerator_value,
							numerator_unit: conc.numerator_unit,
							denominator_value: conc.denominator_value,
							denominator_unit: conc.denominator_unit
						};
					}
				});
			}

			this.setState({ existingConcentrations: concentrationMap }, () => {
				this._populateFormFields();
			});
		} catch (error) {
			console.error('Error loading existing concentrations:', error);
		}
	};

	_populateFormFields = () => {
		setTimeout(() => {
			const { existingConcentrations } = this.state;
			const drugTypes = this.props.drugTypes || [];

			drugTypes.forEach((drugType) => {
				const drugTypeId = drugType.id;
				const concentration = existingConcentrations[drugTypeId];

				if (concentration) {
					const numerator_valueRef = this.myRefs[`${drugTypeId}_numerator_value`];
					const numerator_unitRef = this.myRefs[`${drugTypeId}_numerator_unit`];
					const denominator_valueRef = this.myRefs[`${drugTypeId}_denominator_value`];
					const denominator_unitRef = this.myRefs[`${drugTypeId}_denominator_unit`];

					if (numerator_valueRef) {
						numerator_valueRef.setState({ value: concentration.numerator_value });
					}
					if (numerator_unitRef && numeratorUnitOptions[concentration.numerator_unit]) {
						numerator_unitRef.setState({ value: concentration.numerator_unit, search: concentration.numerator_unit });
					}
					if (denominator_valueRef) {
						denominator_valueRef.setState({ value: concentration.denominator_value });
					}
					if (denominator_unitRef && denominatorUnitOptions[concentration.denominator_unit]) {
						denominator_unitRef.setState({ value: concentration.denominator_unit, search: concentration.denominator_unit });
					}
				}
			});
		}, 100);
	};

	_updateConcentration = (drugTypeId, field, value) => {
		const concentrations = { ...this.state.concentrations };
		if (!concentrations[drugTypeId]) {
			concentrations[drugTypeId] = {};
		}
		concentrations[drugTypeId][field] = value;
		this.setState({ concentrations });
	};

	_confirm = async () => {
		const drugTypes = this.props.drugTypes || [];
		const concentrations = {};
		const { existingConcentrations } = this.state;
		
		try {
			for (let i = 0; i < drugTypes.length; i++) {
				const drugType = drugTypes[i];
				const drugTypeId = drugType.id;
				const numerator_valueRef = this.myRefs[`${drugTypeId}_numerator_value`];
				const numerator_unitRef = this.myRefs[`${drugTypeId}_numerator_unit`];
				const denominator_valueRef = this.myRefs[`${drugTypeId}_denominator_value`];
				const denominator_unitRef = this.myRefs[`${drugTypeId}_denominator_unit`];
				
				if (!numerator_valueRef || !numerator_unitRef || !denominator_valueRef || !denominator_unitRef) continue;
				
				const numerator_value = numerator_valueRef.getValue() || '';
				let numerator_unit = numerator_unitRef.getValue();
				if (numerator_unit === -1) numerator_unit = '';
				
				const denominator_value = denominator_valueRef.getValue() || '';
				let denominator_unit = denominator_unitRef.getValue();
				if (denominator_unit === -1) denominator_unit = '';
				
				if (!numerator_value || !numerator_unit || !denominator_value || !denominator_unit) {
					continue;
				}
				
				const existing = existingConcentrations && existingConcentrations[drugTypeId];
				const isDifferent = !existing || 
					existing.numerator_value !== numerator_value ||
					existing.numerator_unit !== numerator_unit ||
					existing.denominator_value !== denominator_value ||
					existing.denominator_unit !== denominator_unit;
				
				if (!isDifferent) {
					continue;
				}
				
				const concentrationData = {
					numerator_value: numerator_value,
					numerator_unit: numerator_unit,
					denominator_value: denominator_value,
					denominator_unit: denominator_unit
				};
				
				const createdConcentration = await mainAPI.drug_concentration.create(concentrationData);
				
				if (createdConcentration && createdConcentration.id) {
					await mainAPI.allowed_drug.updateByType({
						type: drugTypeId,
						drug_concentration_id: createdConcentration.id
					});
					
					concentrations[drugTypeId] = {
						numerator_value,
						numerator_unit,
						denominator_value,
						denominator_unit,
						formatted: `${numerator_value}${numerator_unit}/${denominator_value}${denominator_unit}`,
						concentration_id: createdConcentration.id
					};
				}
			}
			
			if (Object.keys(concentrations).length > 0) {
				toasts.show('Concentrations saved successfully');
			} else {
				toasts.show('No changes to save');
			}
			
			if (this.props.onComplete) {
				this.props.onComplete(concentrations);
			}
			this.props.onClose();
		} catch (error) {
			console.error('Error saving concentrations:', error);
			toasts.error('Error saving concentrations: ' + (error.message || 'Unknown error'));
		}
	};

	render() {
		const show = this.props.show !== undefined ? this.props.show : true;
		const drugTypes = this.props.drugTypes || [];

		return (
			<Popup {...this.props} show={show} className="g-drug-concentration-popup">
				<Card>
					<FontIcon className="close-icon" onClick={this.props.onClose} name="icon-close" />
					<Label large black center>
						Enter Drug Concentrations
					</Label>
					<Label black center small>
						Format: amount-unit/amount-unit (e.g., 100mcg/2ml)
					</Label>
					<div className="concentration-list">
						{drugTypes.map((drugType) => {
							const drugTypeId = drugType.id;
							return (
								<div key={drugTypeId} className="concentration-item">
									<Label black medium>
										{drugType.name}
									</Label>
									<div className="concentration-inputs">
										<Input
											type="number"
											placeholder="Amount"
											value={(this.state.existingConcentrations && this.state.existingConcentrations[drugTypeId] && this.state.existingConcentrations[drugTypeId].numerator_value) || ''}
											ref={(ref) => (this.myRefs[`${drugTypeId}_numerator_value`] = ref)}
										/>
										<UniversalDropDown
											placeholder="Unit"
											options={numeratorUnitOptions}
											value={(this.state.existingConcentrations && this.state.existingConcentrations[drugTypeId] && this.state.existingConcentrations[drugTypeId].numerator_unit) || -1}
											ref={(ref) => (this.myRefs[`${drugTypeId}_numerator_unit`] = ref)}
										/>
										<Label black>/</Label>
										<Input
											type="number"
											placeholder="Amount"
											value={(this.state.existingConcentrations && this.state.existingConcentrations[drugTypeId] && this.state.existingConcentrations[drugTypeId].denominator_value) || ''}
											ref={(ref) => (this.myRefs[`${drugTypeId}_denominator_value`] = ref)}
										/>
										<UniversalDropDown
											placeholder="Unit"
											options={denominatorUnitOptions}
											value={(this.state.existingConcentrations && this.state.existingConcentrations[drugTypeId] && this.state.existingConcentrations[drugTypeId].denominator_unit) || -1}
											ref={(ref) => (this.myRefs[`${drugTypeId}_denominator_unit`] = ref)}
										/>
									</div>
								</div>
							);
						})}
					</div>
					<div className="actions">
						<SecondaryButton onClick={this.props.onClose}>
							Cancel
						</SecondaryButton>
						<PrimaryButton onClick={this._confirm}>
							Save
						</PrimaryButton>
					</div>
				</Card>
			</Popup>
		);
	}
}




// WEBPACK FOOTER //
// ./src/popups/drugConcentrationPopup/DrugConcentrationPopup.jsx